import { projectId, publicAnonKey } from '/utils/supabase/info';
import type { Project } from '@/data/projects';
import type { NewsItem } from '@/data/news';

export interface ContactInfo {
  address: string;
  phone: string;
  email: string;
  officeLink?: string;
  onlineText?: string;
  companyName?: string;
  companyLocation?: string;
  taxNumber?: string;
  registrationNumber?: string;
}

export interface TeamMember {
  id: string;
  name: string;
  name_en?: string;
  position: string;
  position_en?: string;
  bio: string;
  bio_en?: string;
  photo: string;
  order: number;
}

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-586f2897`;

// Helper function to get auth headers
function getAuthHeaders(token?: string) {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    headers['Authorization'] = `Bearer ${publicAnonKey}`;
  }
  
  return headers;
}

// ========== HEALTH CHECK ==========

export async function checkBackendHealth(): Promise<{ ok: boolean; error?: string }> {
  try {
    console.log('🏥 Checking backend health...');
    console.log('📍 API_BASE:', API_BASE);
    
    const response = await fetch(`${API_BASE}/health`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      console.error('❌ Backend health check failed:', response.status, response.statusText);
      return { ok: false, error: `Backend returned ${response.status}: ${response.statusText}` };
    }
    
    const data = await response.json();
    console.log('✅ Backend is healthy:', data);
    return { ok: true };
  } catch (error) {
    console.error('💥 Backend health check exception:', error);
    return { ok: false, error: String(error) };
  }
}

// ========== PROJECT API ==========

export async function getAllProjects(): Promise<Project[]> {
  try {
    const response = await fetch(`${API_BASE}/projects`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      console.error('Failed to fetch projects:', response.statusText);
      // Return empty array instead of throwing
      return [];
    }
    
    const data = await response.json();
    console.log('API Response - Projects:', data);
    
    // Filter out null/undefined values
    const validProjects = (data.projects || []).filter((p: Project | null) => p !== null && p !== undefined);
    console.log('Valid projects after filtering:', validProjects.length);
    
    return validProjects;
  } catch (error) {
    console.error('Error fetching projects:', error);
    // Return empty array on error
    return [];
  }
}

export async function getProjectById(id: string): Promise<Project | null> {
  try {
    const response = await fetch(`${API_BASE}/projects/${id}`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      console.error('Failed to fetch project:', await response.text());
      return null;
    }
    
    const data = await response.json();
    return data.project;
  } catch (error) {
    console.error('Error fetching project:', error);
    return null;
  }
}

export async function getProjectBySlug(slug: string): Promise<Project | null> {
  try {
    // Get all projects
    const projects = await getAllProjects();
    
    // First, try to find by matching the full generated slug
    const { generateProjectUrl } = await import('@/utils/slug');
    const project = projects.find(p => generateProjectUrl(p) === slug);
    
    if (project) {
      return project;
    }
    
    // Fallback: If not found by full slug, try to find by order number
    // (for backwards compatibility)
    const { extractOrderFromSlug } = await import('@/utils/slug');
    const orderNumber = extractOrderFromSlug(slug);
    const fallbackProject = projects.find(p => (p.order ?? 0) === orderNumber);
    
    return fallbackProject || null;
  } catch (error) {
    console.error('Error fetching project by slug:', error);
    return null;
  }
}

export async function createProject(project: Omit<Project, 'id'>, token: string): Promise<Project | null> {
  try {
    console.log('API createProject - Sending request:', { project, tokenPreview: token.substring(0, 20) + '...' });
    
    // Use DEV endpoint that doesn't require auth
    const response = await fetch(`${API_BASE}/dev/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`, // Use public key for Supabase, but endpoint won't check it
      },
      body: JSON.stringify(project),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API createProject - Failed:', response.status, errorText);
      return null;
    }
    
    const data = await response.json();
    console.log('API createProject - Success:', data);
    return data.project;
  } catch (error) {
    console.error('API createProject - Error:', error);
    return null;
  }
}

export async function updateProject(id: string, updates: Partial<Project>, token: string): Promise<Project | null> {
  try {
    console.log('API updateProject - Sending request:', { id, updates, tokenPreview: token.substring(0, 20) + '...' });
    
    // Use DEV endpoint that doesn't require auth
    const response = await fetch(`${API_BASE}/dev/projects/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`, // Use public key for Supabase, but endpoint won't check it
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API updateProject - Failed:', response.status, errorText);
      return null;
    }
    
    const data = await response.json();
    console.log('API updateProject - Success:', data);
    return data.project;
  } catch (error) {
    console.error('API updateProject - Error:', error);
    return null;
  }
}

export async function deleteProject(id: string, token: string): Promise<boolean> {
  try {
    console.log('API deleteProject - Sending request:', { id, tokenPreview: token.substring(0, 20) + '...' });
    
    // Use DEV endpoint that doesn't require auth
    const response = await fetch(`${API_BASE}/dev/projects/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`, // Use public key for Supabase, but endpoint won't check it
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API deleteProject - Failed:', response.status, errorText);
      return false;
    }
    
    console.log('API deleteProject - Success');
    return true;
  } catch (error) {
    console.error('API deleteProject - Error:', error);
    return false;
  }
}

export async function reorderProjects(projectIds: string[], token: string): Promise<boolean> {
  try {
    console.log('API reorderProjects - Sending request:', { projectIds, tokenPreview: token.substring(0, 20) + '...' });
    
    // Use DEV endpoint that doesn't require auth
    const response = await fetch(`${API_BASE}/dev/projects/reorder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify({ projectIds }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API reorderProjects - Failed:', response.status, errorText);
      return false;
    }
    
    console.log('API reorderProjects - Success');
    return true;
  } catch (error) {
    console.error('API reorderProjects - Error:', error);
    return false;
  }
}

// ========== NEWS API ==========

export async function getAllNews(): Promise<NewsItem[]> {
  try {
    // Use DEV endpoint for development (no auth required)
    const response = await fetch(`${API_BASE}/dev/news`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      console.error('Failed to fetch news:', await response.text());
      return [];
    }
    
    const data = await response.json();
    console.log('API Response - News:', data);
    
    // Filter out null/undefined values
    const validNews = (data.news || []).filter((n: NewsItem | null) => n !== null && n !== undefined);
    console.log('Valid news after filtering:', validNews.length);
    
    return validNews;
  } catch (error) {
    console.error('Error fetching news:', error);
    return [];
  }
}

export async function getNewsById(id: string): Promise<NewsItem | null> {
  try {
    const response = await fetch(`${API_BASE}/news/${id}`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      console.error('Failed to fetch news:', await response.text());
      return null;
    }
    
    const data = await response.json();
    return data.news;
  } catch (error) {
    console.error('Error fetching news:', error);
    return null;
  }
}

export async function getNewsBySlug(slug: string): Promise<NewsItem | null> {
  try {
    // Extract ID from slug (last part after last hyphen)
    const { extractIdFromNewsSlug } = await import('@/utils/slug');
    const newsId = extractIdFromNewsSlug(slug);
    
    // Get all news and find by ID
    const newsList = await getAllNews();
    
    // Find news where the ID matches
    const news = newsList.find(n => n.id === newsId);
    
    console.log('getNewsBySlug - slug:', slug, 'extracted ID:', newsId, 'found:', !!news);
    
    return news || null;
  } catch (error) {
    console.error('Error fetching news by slug:', error);
    return null;
  }
}

export async function createNews(news: Omit<NewsItem, 'id'>, token: string): Promise<NewsItem | null> {
  try {
    console.log('API createNews - Sending request:', { news, tokenPreview: token.substring(0, 20) + '...' });
    
    // Use DEV endpoint that doesn't require auth
    const response = await fetch(`${API_BASE}/dev/news`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`, // Use public key for Supabase, but endpoint won't check it
      },
      body: JSON.stringify(news),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API createNews - Failed:', response.status, errorText);
      return null;
    }
    
    const data = await response.json();
    console.log('API createNews - Success:', data);
    return data.news;
  } catch (error) {
    console.error('API createNews - Error:', error);
    return null;
  }
}

export async function updateNews(id: string, updates: Partial<NewsItem>, token: string): Promise<NewsItem | null> {
  try {
    console.log('API updateNews - Sending request:', { id, updates, tokenPreview: token.substring(0, 20) + '...' });
    
    // Use DEV endpoint that doesn't require auth
    const response = await fetch(`${API_BASE}/dev/news/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`, // Use public key for Supabase, but endpoint won't check it
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API updateNews - Failed:', response.status, errorText);
      return null;
    }
    
    const data = await response.json();
    console.log('API updateNews - Success:', data);
    return data.news;
  } catch (error) {
    console.error('API updateNews - Error:', error);
    return null;
  }
}

export async function deleteNews(id: string, token: string): Promise<boolean> {
  try {
    console.log('API deleteNews - Sending request:', { id, tokenPreview: token.substring(0, 20) + '...' });
    
    // Use DEV endpoint that doesn't require auth
    const response = await fetch(`${API_BASE}/dev/news/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`, // Use public key for Supabase, but endpoint won't check it
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API deleteNews - Failed:', response.status, errorText);
      return false;
    }
    
    console.log('API deleteNews - Success');
    return true;
  } catch (error) {
    console.error('API deleteNews - Error:', error);
    return false;
  }
}

// ========== SEED API ==========

export async function seedDatabase(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/seed`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      const error = await response.json();
      console.error('Failed to seed database:', error);
      return false;
    }
    
    const data = await response.json();
    console.log('Database seeded:', data);
    return true;
  } catch (error) {
    console.error('Error seeding database:', error);
    return false;
  }
}

// ========== SETTINGS API ==========

export interface SiteSettings {
  adminMenuVisible: boolean;
  newsEnabled: boolean;
  englishEnabled: boolean;
}

export async function getSiteSettings(): Promise<SiteSettings> {
  try {
    const response = await fetch(`${API_BASE}/settings`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      console.error('Failed to fetch settings:', await response.text());
      return {
        adminMenuVisible: true,
        newsEnabled: true,
        englishEnabled: true
      };
    }
    
    const data = await response.json();
    return data.settings || {
      adminMenuVisible: true,
      newsEnabled: true,
      englishEnabled: true
    };
  } catch (error) {
    console.error('Error fetching settings:', error);
    return {
      adminMenuVisible: true,
      newsEnabled: true,
      englishEnabled: true
    };
  }
}

export async function updateSiteSettings(settings: SiteSettings, token: string): Promise<SiteSettings | null> {
  try {
    const response = await fetch(`${API_BASE}/settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify({ settings }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      console.error('Failed to update settings:', error);
      return null;
    }
    
    const data = await response.json();
    return data.settings;
  } catch (error) {
    console.error('Error updating settings:', error);
    return null;
  }
}

// ========== CATEGORY API ==========

export interface Category {
  id: string;
  name: {
    hu: string;
    en: string;
  };
}

export async function getAllCategories(): Promise<Category[]> {
  try {
    const response = await fetch(`${API_BASE}/categories`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      console.error('Failed to fetch categories:', await response.text());
      return [];
    }
    
    const data = await response.json();
    return data.categories || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
}

export async function updateCategories(categories: Category[], token: string): Promise<Category[] | null> {
  try {
    const response = await fetch(`${API_BASE}/dev/categories`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify({ categories }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      console.error('Failed to update categories:', error);
      return null;
    }
    
    const data = await response.json();
    return data.categories;
  } catch (error) {
    console.error('Error updating categories:', error);
    return null;
  }
}

// ========== CONTENT API ==========

export interface AboutContent {
  hu: string;
  en: string;
}

export async function getAboutContent(): Promise<AboutContent | null> {
  try {
    const response = await fetch(`${API_BASE}/content/about`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
    });
    
    if (!response.ok) {
      console.error('Failed to fetch about content, response not ok');
      return getDefaultAboutContent();
    }
    
    const data = await response.json();
    console.log('API - getAboutContent response:', data);
    
    // Return default content if none exists
    if (!data.content) {
      console.log('API - No content found, returning default');
      return getDefaultAboutContent();
    }
    
    return data.content;
  } catch (error) {
    console.error('Error fetching about content:', error);
    return getDefaultAboutContent();
  }
}

function getDefaultAboutContent(): AboutContent {
  return {
    hu: '<p>A <strong>ZSALAKÓ Építőműhely</strong> a kortárs design és a hagyományos kézművesség találkozásából jön létre. Több mint 15 éves tapasztalattal rendelkezünk a kiváló minőségű építészeti megoldások tervezésében.</p><p>Szakértő csapatunk a funkcionális és esztétikus terek létrehozására specializálódott, amelyek tükrözik ügyfeleink egyedi vízióit és igényeit. Hiszünk abban, hogy minden projekt egyedi történetet mesél, és elkötelezettek vagyunk amellett, hogy ezt a történetet életre keltsük.</p>',
    en: '<p><strong>ZSALAKÓ Építőműhely</strong> is born from the intersection of contemporary design and traditional craftsmanship. We have over 15 years of experience in designing high-quality architectural solutions.</p><p>Our expert team specializes in creating functional and aesthetic spaces that reflect our clients\' unique visions and needs. We believe that every project tells a unique story, and we are committed to bringing that story to life.</p>'
  };
}

export async function updateAboutContent(content: AboutContent, token: string): Promise<AboutContent | null> {
  try {
    console.log('API updateAboutContent - Sending request:', { content, tokenPreview: token.substring(0, 20) + '...' });
    
    // Use DEV endpoint that doesn't require auth
    const response = await fetch(`${API_BASE}/dev/content/about`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify(content),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API updateAboutContent - Failed:', response.status, errorText);
      return null;
    }
    
    const data = await response.json();
    console.log('API updateAboutContent - Success:', data);
    return data.content;
  } catch (error) {
    console.error('API updateAboutContent - Error:', error);
    return null;
  }
}

export async function getContactInfo(): Promise<ContactInfo | null> {
  try {
    const response = await fetch(`${API_BASE}/content/contact`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
    });
    
    if (!response.ok) {
      console.error('Failed to fetch contact info, response not ok');
      return getDefaultContactInfo();
    }
    
    const data = await response.json();
    console.log('API - getContactInfo response:', data);
    
    // Return default content if none exists
    if (!data.content) {
      console.log('API - No contact info found, returning default');
      return getDefaultContactInfo();
    }
    
    return data.content;
  } catch (error) {
    console.error('Error fetching contact info:', error);
    return getDefaultContactInfo();
  }
}

function getDefaultContactInfo(): ContactInfo {
  return {
    email: 'info@zsalako.hu',
    phone: '+36 1 234 5678',
    address: 'Budapest, 1053 Kossuth Lajos utca 1.'
  };
}

export async function updateContactInfo(contact: ContactInfo, token: string): Promise<ContactInfo | null> {
  try {
    console.log('API updateContactInfo - Sending request:', { contact, tokenPreview: token.substring(0, 20) + '...' });
    
    // Use DEV endpoint that doesn't require auth
    const response = await fetch(`${API_BASE}/dev/content/contact`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify(contact),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API updateContactInfo - Failed:', response.status, errorText);
      return null;
    }
    
    const data = await response.json();
    console.log('API updateContactInfo - Success:', data);
    return data.content;
  } catch (error) {
    console.error('API updateContactInfo - Error:', error);
    return null;
  }
}

// ========== TEAM API ==========

export async function getTeamMembers(): Promise<TeamMember[]> {
  try {
    const response = await fetch(`${API_BASE}/dev/team`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
    });
    
    if (!response.ok) {
      console.error('Failed to fetch team members, response not ok');
      return [];
    }
    
    const data = await response.json();
    console.log('API - getTeamMembers response:', data);
    
    return data.members || [];
  } catch (error) {
    console.error('Error fetching team members:', error);
    return [];
  }
}

export async function createTeamMember(member: Omit<TeamMember, 'id'>, token: string): Promise<TeamMember | null> {
  try {
    console.log('API createTeamMember - Sending request:', { member, tokenPreview: token.substring(0, 20) + '...' });
    
    const response = await fetch(`${API_BASE}/dev/team`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify(member),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API createTeamMember - Failed:', response.status, errorText);
      return null;
    }
    
    const data = await response.json();
    console.log('API createTeamMember - Success:', data);
    return data.member;
  } catch (error) {
    console.error('API createTeamMember - Error:', error);
    return null;
  }
}

export async function updateTeamMember(id: string, updates: Partial<TeamMember>, token: string): Promise<TeamMember | null> {
  try {
    console.log('API updateTeamMember - Sending request:', { id, updates, tokenPreview: token.substring(0, 20) + '...' });
    
    const response = await fetch(`${API_BASE}/dev/team/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API updateTeamMember - Failed:', response.status, errorText);
      return null;
    }
    
    const data = await response.json();
    console.log('API updateTeamMember - Success:', data);
    return data.member;
  } catch (error) {
    console.error('API updateTeamMember - Error:', error);
    return null;
  }
}

export async function deleteTeamMember(id: string, token: string): Promise<boolean> {
  try {
    console.log('API deleteTeamMember - Sending request:', { id, tokenPreview: token.substring(0, 20) + '...' });
    
    const response = await fetch(`${API_BASE}/dev/team/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API deleteTeamMember - Failed:', response.status, errorText);
      return false;
    }
    
    console.log('API deleteTeamMember - Success');
    return true;
  } catch (error) {
    console.error('API deleteTeamMember - Error:', error);
    return false;
  }
}

function getDefaultTeamMembers(): TeamMember[] {
  return [];
}

export async function updateTeamMembers(team: TeamMember[], token: string): Promise<TeamMember[] | null> {
  try {
    console.log('API updateTeamMembers - Sending request:', { team, tokenPreview: token.substring(0, 20) + '...' });
    
    // Use DEV endpoint that doesn't require auth
    const response = await fetch(`${API_BASE}/dev/content/team`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify({ team }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API updateTeamMembers - Failed:', response.status, errorText);
      return null;
    }
    
    const data = await response.json();
    console.log('API updateTeamMembers - Success:', data);
    return data.content;
  } catch (error) {
    console.error('API updateTeamMembers - Error:', error);
    return null;
  }
}

// ========== IMAGE UPLOAD API ==========

export async function uploadImage(file: File): Promise<string | null> {
  try {
    console.log('API uploadImage - Uploading file:', file.name, file.type, file.size);
    
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE}/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('API uploadImage - Failed:', response.status, errorData);
      
      // Show detailed error message
      const errorMessage = errorData.details || errorData.error || 'Unknown error';
      const hint = errorData.hint ? `\n\nTipp: ${errorData.hint}` : '';
      throw new Error(`${errorMessage}${hint}`);
    }
    
    const data = await response.json();
    console.log('API uploadImage - Success:', data);
    return data.url;
  } catch (error) {
    console.error('API uploadImage - Error:', error);
    // Re-throw the error so it can be caught and shown to the user
    throw error;
  }
}